import { SURVEY_QUESTIONS, type Question, type QuestionOption } from "./survey-config";
import type { AdminResponse } from "./api";
import { durationShort, isoZ, todayIsoDate } from "./format";

export const SURVEY_VERSION = 1;

function questionTypeLabel(q: Question): string {
  const optional = q.required === false ? ", optional" : "";
  if (q.type === "slider") return `slider, ${q.min}–${q.max}${optional}`;
  return `${q.type}${optional}`;
}

function lookupOption(q: Question, value: string): QuestionOption | undefined {
  if (q.type !== "singleSelect" && q.type !== "multiSelect") return undefined;
  return q.options.find((o) => o.value === value);
}

function blockquote(text: string): string {
  if (!text || !text.trim()) return "> _(nicht beantwortet)_";
  return text
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => `> ${line}`)
    .join("\n");
}

function getAnswer(r: AdminResponse, key: string): unknown {
  const a = r.answers as unknown as Record<string, unknown>;
  return a[key];
}

function renderTextAnswer(r: AdminResponse, q: Question): string {
  const v = getAnswer(r, q.id);
  const text = typeof v === "string" ? v : "";
  return ["**Antwort:**", blockquote(text)].join("\n");
}

function renderSingleSelect(r: AdminResponse, q: Extract<Question, { type: "singleSelect" }>): string {
  const v = getAnswer(r, q.id);
  if (typeof v !== "string" || !v) return "**Antwort:** _(nicht beantwortet)_";
  const opt = lookupOption(q, v);
  return `**Antwort:** \`${v}\`${opt ? ` — ${opt.label}` : ""}`;
}

function renderMultiSelect(
  r: AdminResponse,
  q: Extract<Question, { type: "multiSelect" }>,
): string {
  const v = getAnswer(r, q.id);
  const otherKey = `${q.id}_other`;
  const other = getAnswer(r, otherKey);
  const selected = Array.isArray(v) ? (v as string[]) : [];

  const lines: string[] = ["**Ausgewählt:**"];
  if (selected.length === 0) {
    lines.push("- _(keine Auswahl)_");
  } else {
    for (const value of selected) {
      const opt = lookupOption(q, value);
      lines.push(`- \`${value}\`${opt ? ` — ${opt.label}` : ""}`);
    }
  }
  lines.push("");
  if (q.allowOther) {
    const otherText = typeof other === "string" ? other.trim() : "";
    lines.push(`**Sonstiges:** ${otherText ? otherText : "_(leer)_"}`);
  }
  return lines.join("\n");
}

function renderSlider(r: AdminResponse, q: Extract<Question, { type: "slider" }>): string {
  const v = getAnswer(r, q.id);
  if (typeof v !== "number") return "**Antwort:** _(nicht beantwortet)_";
  return `**Antwort:** ${v} / ${q.max}`;
}

function renderQuestionBlock(r: AdminResponse, q: Question, idx: number): string {
  const heading = `#### F${idx + 1} — ${q.id} (${questionTypeLabel(q)})`;
  const question = `**Frage:** ${q.title}`;
  let body: string;
  switch (q.type) {
    case "shortText":
    case "longText":
      body = renderTextAnswer(r, q);
      break;
    case "singleSelect":
      body = renderSingleSelect(r, q);
      break;
    case "multiSelect":
      body = renderMultiSelect(r, q);
      break;
    case "slider":
      body = renderSlider(r, q);
      break;
  }
  return [heading, "", question, "", body].join("\n");
}

function renderParticipantSection(r: AdminResponse): string {
  const header = [
    `### Teilnehmer: ${r.participant}`,
    "",
    `- **Submission-ID:** \`${r.submissionId}\``,
    `- **Gestartet:** ${isoZ(r.startedAt)}`,
    `- **Eingereicht:** ${isoZ(r.submittedAt)}`,
    `- **Bearbeitungsdauer:** ${r.durationMs} ms`,
    "",
  ].join("\n");

  const blocks = SURVEY_QUESTIONS.map((q, i) => renderQuestionBlock(r, q, i)).join("\n\n");
  return `${header}\n${blocks}`;
}

function renderOverviewTable(responses: AdminResponse[]): string {
  const header = [
    "| Participant | Submitted | Dauer | OS | KI-Erfahrung |",
    "|---|---|---|---|---|",
  ];
  const rows = responses.map(
    (r) =>
      `| ${r.participant} | ${isoZ(r.submittedAt)} | ${durationShort(r.durationMs)} | ${
        r.operatingSystem ?? ""
      } | ${r.aiExperience ?? ""} |`,
  );
  return [...header, ...rows].join("\n");
}

function renderAppendix(responses: AdminResponse[]): string {
  const payload = responses.map((r) => ({
    participant: r.participant,
    submissionId: r.submissionId,
    submittedAt: isoZ(r.submittedAt),
    durationMs: r.durationMs,
    answers: r.answers,
  }));
  return ["```json", JSON.stringify(payload, null, 2), "```"].join("\n");
}

export function buildMarkdownExport(responses: AdminResponse[]): string {
  const sections: string[] = [];
  sections.push("# Workshop-Survey-Export");
  sections.push("");
  sections.push(`- **Exportiert am:** ${isoZ(new Date())}`);
  sections.push(`- **Anzahl Teilnehmer:** ${responses.length}`);
  sections.push(`- **Survey-Version:** ${SURVEY_VERSION}`);
  sections.push("");
  sections.push("---");
  sections.push("");
  sections.push("## Teilnehmer-Übersicht");
  sections.push("");
  sections.push(renderOverviewTable(responses));
  sections.push("");
  sections.push("---");
  sections.push("");
  sections.push("## Antworten pro Teilnehmer");
  sections.push("");

  responses.forEach((r, i) => {
    sections.push(renderParticipantSection(r));
    if (i < responses.length - 1) {
      sections.push("");
      sections.push("---");
      sections.push("");
    }
  });

  sections.push("");
  sections.push("---");
  sections.push("");
  sections.push("## Maschinenlesbarer Anhang");
  sections.push("");
  sections.push(renderAppendix(responses));
  sections.push("");

  return sections.join("\n");
}

export function buildJsonExport(responses: AdminResponse[]): string {
  return JSON.stringify(responses, null, 2);
}

export function downloadBlob(filename: string, mime: string, content: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function exportFilenames() {
  const date = todayIsoDate();
  return {
    json: `survey-responses-${date}.json`,
    md: `survey-export-${date}.md`,
  };
}
